import { useEffect } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';

import { buildDeferredParamPatch } from '../lib/dashboard-params';
import type { DashboardParam } from '../types';

/**
 * Once dynamic options have loaded, seed missing params and auto-correct invalid option values in the
 * URL — preferring a spec `default` present in options (not always options[0]). See AI-1186.
 * Shared by the private and public dashboard views; only the `enabled` guard differs.
 */
export function useDeferredParamDefaults(
  enabled: boolean,
  parameters: DashboardParam[],
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
): void {
  useEffect(() => {
    if (!enabled) return;
    const patch = buildDeferredParamPatch(searchParams, parameters);
    if (Object.keys(patch).length === 0) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (Array.isArray(v)) {
            next.delete(k);
            for (const val of v) next.append(k, val);
          } else {
            next.set(k, v);
          }
        }
        return next;
      },
      { replace: true },
    );
  }, [enabled, parameters, searchParams, setSearchParams]);
}
